import React from 'react';

import appendQuery from 'append-query';
import { connect } from 'react-redux';

import { Impression } from '@nerdwallet/analytics/react/impression';
import classes from '@nerdwallet/base-styles/classes';
import { Stack, Box } from '@nerdwallet/currency';

import {
  ENTITY_NAME,
  EVENT_TYPE,
  FORM_NAME,
  SECTION_NAME,
} from '../../../../lib/analytics-constants';
import transformToMrtInputs from '../../../../lib/transform-to-mrt-inputs';
import BaseControlPanel from '../../../config-builder-free/control-panel/control-panel';
import MarketplaceCta from '../../../config-builder-free/marketplace-cta/marketplace-cta';
import MonthlyPayment from '../../../monthly-payment/monthly-payment';
import {
  MORTGAGE,
  MORTGAGE_PAYMENT,
  MORTGAGE_STATE,
} from '../analytics-constants';
import {
  AnnualInterestRateInput,
  LoanTermInput,
  PurchaseLocationInput,
} from '../inputs';
import type { MortgageCalculators } from '../types';

import {
  ANNUAL_INTEREST_RATE_ANALYTICS_CONFIG,
  LOAN_TERM_ANALYTICS_CONFIG,
  LOCATION_ANALYTICS_CONFIG,
} from './analytics';

import styles from './mc-control-panel.module.less';

interface ControlPanelProps {
  mortgageCalculator: MortgageCalculators;
}

interface OwnProps extends ControlPanelProps {
  /** Derived from redux */
  purchaseAddressCity: string;
  /** Derived from redux */
  purchaseAddressState: string;
  /** Derived from redux */
  impressionId: string;
}

const MRT_URL = '/mortgages/mortgage-rates';

const TRK_VALUE_HAS = {
  [MORTGAGE]: 'mc_mrt_cta',
  [MORTGAGE_PAYMENT]: 'mpc_mrt_cta',
  [MORTGAGE_STATE]: 'mc_mrt_cta',
};

const IMPRESSION_KEY_HASH = {
  [MORTGAGE]: 'mortgages-mc_mrt_cta',
  [MORTGAGE_PAYMENT]: 'mortgages-mpc_mrt_cta',
  [MORTGAGE_STATE]: 'mortgages-mc_mrt_cta',
};

const MCControlPanel = ({
  mortgageCalculator,
  purchaseAddressCity,
  purchaseAddressState,
  impressionId,
}: OwnProps): JSX.Element => {
  const getUrl = (data) =>
    appendQuery(MRT_URL, {
      ...transformToMrtInputs(data),
      bypass: true,
      trk: TRK_VALUE_HAS[mortgageCalculator],
    });

  const impressionKey = IMPRESSION_KEY_HASH[mortgageCalculator];

  const panelContent = (
    <Impression
      eventType={EVENT_TYPE.FORM_IMPRESSION}
      entityName={ENTITY_NAME.MRT_CALCULATOR_LATEST_RATES}
      payload={{
        section_name: SECTION_NAME.MRT_CALCULATOR_MONTHLY_PAYMENT,
        form_name: FORM_NAME.MRT_CALCULATOR_LATEST_RATES,
      }}
    >
      <Box paddingX="2" className={styles['mortgage-control-page__wrapper']}>
        <Stack spacing={3}>
          <MarketplaceCta
            title="See latest mortgage rates"
            subtitle={`Get personalized mortgage rates from ${purchaseAddressCity}, ${purchaseAddressState}.`}
            buttonText="CHECK RATES"
            url={getUrl(mortgageCalculator)}
            elementName={TRK_VALUE_HAS[mortgageCalculator]}
            impressionKey={impressionKey}
            impressionId={impressionId}
            className={classes('display-none', 'display-block--wide')}
          />
          <MonthlyPayment calculator="mortgage" view="ControlPanel" />
          <Box>
            <PurchaseLocationInput
              label="Location"
              analytics={LOCATION_ANALYTICS_CONFIG}
            />
          </Box>
          <AnnualInterestRateInput
            label="Interest rate"
            tooltip="The cost of borrowing money that’s typically expressed as an annual percentage of the loan."
            fullWidth
            analytics={ANNUAL_INTEREST_RATE_ANALYTICS_CONFIG}
          />
          <LoanTermInput analytics={LOAN_TERM_ANALYTICS_CONFIG} />
        </Stack>
      </Box>
    </Impression>
  );

  return <BaseControlPanel panelContent={panelContent} />;
};

const mapStateToProps = (state, ownProps: ControlPanelProps) => ({
  impressions: state?.impressions,
  purchaseAddressCity: state?.mortgageCalculator?.inputs?.purchaseAddress?.city,
  purchaseAddressState:
    state?.mortgageCalculator?.inputs?.purchaseAddress?.state,
  impressionId:
    state?.impressions?.[IMPRESSION_KEY_HASH[ownProps.mortgageCalculator]]
      ?.elementImpressionHeader?.impressionId,
});

export default connect(mapStateToProps)(MCControlPanel);
